import React, { createContext, useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import useAuth from '../hooks/useAuth';
import {
  getAllFeedback,
  getFeedbackById,
  createFeedback,
  updateFeedback,
  deleteFeedback
} from '../services/feedbackService';
import Notification from '../components/Common/Notification';

export const FeedbackContext = createContext();

export const FeedbackProvider = ({ children }) => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [feedbackRecords, setFeedbackRecords] = useState([]);
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [notification, setNotification] = useState({ message: '', isError: false });

  const fetchAuthHeaders = () => {
    const storedUser = localStorage.getItem('user');
    const token = user?.token || (storedUser ? JSON.parse(storedUser).token : '');

    if (!token) {
      console.error('User or token not available');
      return {};
    }

    return {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
  };

  const showNotification = (message, isError) => {
    setNotification({ message, isError });
  };

  const closeNotification = () => {
    setNotification({ message: '', isError: false });
  };

  const fetchAllFeedbackRecords = async () => {
    try {
      const authHeaders = fetchAuthHeaders();
      const data = await getAllFeedback(authHeaders);
      setFeedbackRecords(data);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || 'Error fetching feedback records.';
      showNotification(errorMessage, true);
    }
  };

  const fetchFeedbackById = async (id) => {
    try {
      const authHeaders = fetchAuthHeaders();
      const data = await getFeedbackById(id, authHeaders);
      setSelectedFeedback(data);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || `Error fetching feedback record with ID ${id}.`;
      showNotification(errorMessage, true);
    }
  };

  const addFeedbackRecord = async (feedbackData) => {
    try {
      const authHeaders = fetchAuthHeaders();
      const newFeedback = await createFeedback(feedbackData, authHeaders);
      setFeedbackRecords(prevFeedbackRecords => [...prevFeedbackRecords, newFeedback]);
      showNotification('Feedback record added successfully!', false);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || 'Error adding feedback record.';
      showNotification(errorMessage, true);
    }
  };

  const editFeedbackRecord = async (id, feedbackData) => {
    try {
      const authHeaders = fetchAuthHeaders();
      const updatedFeedback = await updateFeedback(id, feedbackData, authHeaders);
      setFeedbackRecords(prevFeedbackRecords =>
        prevFeedbackRecords.map(fb => (fb.id === id ? updatedFeedback : fb))
      );
      showNotification('Feedback record updated successfully!', false);
      return updatedFeedback;
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || 'Error updating feedback record.';
      showNotification(errorMessage, true);
      throw error;
    }
  };

  const removeFeedbackRecord = async (id) => {
    try {
      const authHeaders = fetchAuthHeaders();
      await deleteFeedback(id, authHeaders);
      setFeedbackRecords(prevFeedbackRecords => prevFeedbackRecords.filter(fb => fb.id !== id));
      showNotification('Feedback record removed successfully!', false);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || 'Error removing feedback record.';
      showNotification(errorMessage, true);
    }
  };

  // useEffect(() => {
  //   fetchAllFeedbackRecords();
  // }, []);


  useEffect(() => {
    if (!isAuthenticated && !['/', '/login'].includes(window.location.pathname)) {
      navigate('/login');
    }
  }, [isAuthenticated, navigate]);


  return (
    <FeedbackContext.Provider
      value={{
        feedbackRecords,
        selectedFeedback,
        fetchAllFeedbackRecords,
        fetchFeedbackById,
        addFeedbackRecord,
        editFeedbackRecord,
        removeFeedbackRecord,
      }}
    >
      {children}
      {notification.message && (
        <Notification
          message={notification.message}
          isError={notification.isError}
          onClose={closeNotification}
        />
      )}
    </FeedbackContext.Provider>
  );
};
